/**
 *
 * Created Date: 2019-07-29, 16:33:23 (zhenliang.sun)
 * Last Modified: 2019-07-29, 17:06:12 (zhenliang.sun)
 * Email: zhenliang.sun@gmail.com
 *
 * Distributed under the MIT license. See LICENSE file for details.
 * Copyright (c) 2019 vhall
 */
import DocModuleDB from './DB';
import { modifyError, parse } from './DBUtil';

export class DBStore {
  constructor(options = {}) {
    const { store, db } = options;

    if (typeof store !== 'object' || !store.name || typeof store.name !== 'string') {
      throw new Error('[DBStore]: options.store should be a store config object.');
    }

    if (!(db instanceof DocModuleDB)) {
      throw new Error('[DBStore]: options.db should be an instanceof DocModuleDB.');
    }

    this.store = store;
    this.db = db;
    this.name = store.name;
    this.keyPath = store.isKeyValue ? 'key' : store.keyPath;
  }

  transaction(writable = false) {
    const name = this.name;
    const mode = writable ? 'readwrite' : 'readonly';
    return this.db.connect().then(db => db.transaction(name, mode));
  }
  objectStore(writable = false) {
    const name = this.name;
    return this.transaction(writable).then(tx => tx.objectStore(name));
  }
  request(create, writable = false) {
    return new Promise((resolve, reject) => {
      this.objectStore(writable).then(objectStore => {
        const request = create(objectStore);
        request.onsuccess = e => {
          const result = e.target.result;
          resolve(result);
        };
        request.onerror = e => {
          reject(modifyError(e));
        };
      });
    });
  }
  cursor(options) {
    const { index, range, direction, onTouch, onDone, onError, writable = false } = options;
    return this.objectStore(writable).then(objectStore => {
      const owner = index ? objectStore.index(index) : objectStore;
      const request = owner.openCursor(range, direction);
      request.onsuccess = e => {
        const cursor = e.target.result;
        if (cursor) {
          onTouch(cursor, owner);
        } else {
          onDone(cursor, owner);
        }
      };
      request.onerror = e => {
        onError(modifyError(e));
      };
    });
  }
  iterate(fn, options = {}) {
    const { writable = false, direction = 'next' } = options;
    return new Promise((resolve, reject) => {
      this.cursor({
        writable,
        direction,
        onTouch: (cursor, owner) => {
          const next = () => cursor.continue();
          const stop = () => {
            owner.transaction.abort();
            resolve();
          };
          fn(cursor, next, stop);
        },
        onDone: () => {
          resolve();
        },
        onError: e => {
          reject(e);
        }
      });
    });
  }
  batch(fns, options = {}) {
    const { writable = true } = options;
    return this.transaction(writable).then(tx => {
      const name = this.name;
      const promises = [];
      fns.forEach(fn => {
        const deferer = new Promise((resolve, reject) => {
          const objectStore = tx.objectStore(name);
          const request = fn(objectStore);
          request.onsuccess = e => {
            const result = e.target.result;
            resolve(result);
          };
          request.onerror = e => {
            reject(modifyError(e));
          };
        });
        promises.push(deferer);
      });
      return Promise.all(promises);
    });
  }

  // ==========================================

  get(key) {
    // single key
    if (!Array.isArray(key)) {
      return this.request(objectStore => objectStore.get(key));
    }

    // multiple keys
    const keys = key;
    const fns = keys.map(key => objectStore => objectStore.get(key));
    return this.batch(fns, { writable: false });
  }
  keys() {
    const keyPah = this.keyPath;
    const results = [];
    return this.each(obj => {
      const key = parse(obj, keyPah);
      results.push(key);
    }).then(() => {
      return results;
    });
  }
  all() {
    const results = [];
    return this.each(obj => {
      results.push(obj);
    }).then(() => {
      return results;
    });
  }
  count() {
    return this.request(objectStore => objectStore.count());
  }
  // ==========================================
  each(fn) {
    return this.iterate((cursor, next) => {
      const obj = cursor.value;
      fn(obj);
      next();
    });
  }
  reverse(fn) {
    return this.iterate(
      (cursor, next) => {
        const obj = cursor.value;
        fn(obj);
        next();
      },
      { direction: 'prev' }
    );
  }
  some(count = 10, offset = 0) {
    return new Promise((resolve, reject) => {
      let results = [];
      let i = 0;
      let start = offset;
      let end = offset + count;
      let direction;

      // offset < 0, means beginning from the latest item,
      // for example, offset = -1, means beginning from the last item
      if (offset < 0) {
        direction = 'prev';
        count = Math.min(count, -offset);
        start = -(offset + count) || 0;
        end = start + count;
      }

      this.iterate(
        (cursor, next, stop) => {
          if (i < start) {
            i += 1;
            next();
          } else if (i < end) {
            results.push(cursor.value);
            i += 1;
            next();
          } else {
            stop();
          }
        },
        { direction }
      )
        .then(() => {
          if (offset < 0) {
            results.reverse();
          }
          resolve(results);
        })
        .catch(reject);
    });
  }
  first() {
    return this.some(1).then(items => items[0]);
  }
  last() {
    return this.some(1, -1).then(items => items[0]);
  }
  // =========================
  find(key, value) {
    return this.request(objectStore => objectStore.index(key).get(value));
  }
  query(key, value, compare) {
    const range = (function () {
      switch (compare) {
        case '>':
          return IDBKeyRange.lowerBound(value, true);
        case '>=':
          return IDBKeyRange.lowerBound(value);
        case '<':
          return IDBKeyRange.upperBound(value, true);
        case '<=':
          return IDBKeyRange.upperBound(value);
        case '%':
        case '!=':
        case 'in':
          return undefined;
        default:
          return IDBKeyRange.only(value);
      }
    })();

    const results = [];
    return new Promise((resolve, reject) => {
      this.cursor({
        index: key,
        range,
        onTouch: (cursor, owner) => {
          let targetObj = cursor.value;
          let keyPath = owner.keyPath;
          let targetValue = parse(targetObj, keyPath);

          if (compare === '!=') {
            if (targetValue !== value) {
              results.push(targetObj);
            }
          } else if (compare === '%') {
            if (typeof targetValue === 'string' && targetValue.indexOf(value) > -1) {
              results.push(targetObj);
            }
          } else if (compare === 'in') {
            if (Array.isArray(value) && value.indexOf(targetValue) > -1) {
              results.push(targetObj);
            }
          } else {
            results.push(targetObj);
          }

          cursor.continue();
        },
        onDone: () => {
          resolve(results);
        },
        onError: e => {
          reject(e);
        }
      });
    });
  }
  select(conditions) {
    const currentStore = this.store;
    const indexes = currentStore.indexes || [];
    const indexesMapping = {};
    indexes.forEach(item => {
      let { name, keyPath } = item;
      indexesMapping[name] = keyPath;
    });

    // eslint-disable-next-line camelcase
    const or_conditions = [];
    // eslint-disable-next-line camelcase
    const and_conditions = [];
    for (let i = 0, len = conditions.length; i < len; i += 1) {
      let { key, value, compare, optional } = conditions[i];
      let keyPath = indexesMapping[key] || key; // if there is not such index, use original key as keyPath
      if (optional) {
        or_conditions.push({ keyPath, value, compare });
      } else {
        and_conditions.push({ keyPath, value, compare });
      }
    }
    const determine = function (obj) {
      let compareAAndB = function (a, b, compare) {
        if (a === undefined) {
          return false;
        }
        switch (compare) {
          case '>':
            return a > b;
          case '>=':
            return a >= b;
          case '<':
            return a < b;
          case '<=':
            return a <= b;
          case '!=':
            return a !== b;
          case '%':
            return typeof a === 'string' && a.indexOf(b) > -1;
          case 'in':
            return Array.isArray(b) && b.indexOf(a) > -1;
          default:
            return a === b;
        }
      };
      for (let i = 0, len = and_conditions.length; i < len; i += 1) {
        let { keyPath, value, compare } = and_conditions[i];
        let current = parse(obj, keyPath);
        if (!compareAAndB(current, value, compare)) {
          return false;
        }
      }
      for (let i = 0, len = or_conditions.length; i < len; i += 1) {
        let { keyPath, value, compare } = or_conditions[i];
        let current = parse(obj, keyPath);
        if (compareAAndB(current, value, compare)) {
          return true;
        }
      }
      return false;
    };

    const results = [];
    return this.each(obj => {
      if (determine(obj)) {
        results.push(obj);
      }
    }).then(() => {
      return results;
    });
  }

  // =====================================

  add(obj) {
    if (!Array.isArray(obj)) {
      return this.request(objectStore => objectStore.add(obj), 'readwrite');
    }

    const objs = obj;
    const fns = objs.map(obj => objectStore => objectStore.add(obj));
    return this.batch(fns);
  }
  put(obj) {
    // single object
    if (!Array.isArray(obj)) {
      return this.request(objectStore => objectStore.put(obj), 'readwrite');
    }

    // multiple objects
    const objs = obj;
    const fns = objs.map(obj => objectStore => objectStore.put(obj));
    return this.batch(fns);
  }
  delete(key) {
    // single key
    if (!Array.isArray(key)) {
      return this.request(objectStore => objectStore.delete(key), 'readwrite');
    }

    // multiple keys
    const keys = key;
    const fns = keys.map(key => objectStore => objectStore.delete(key));
    return this.batch(fns);
  }
  remove(obj) {
    const keyPah = this.keyPath;

    // single obj
    if (!Array.isArray(obj)) {
      const key = parse(obj, keyPah);
      return this.delete(key);
    }

    // multiple objects
    const objs = obj;
    const fns = objs.map(obj => {
      const key = parse(obj, keyPah);
      return objectStore => objectStore.delete(key);
    });
    return this.batch(fns);
  }
  clear() {
    return this.request(objectStore => objectStore.clear(), 'readwrite');
  }
}
